import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  SaveSelfAssessmentResultsDocument,
  SelfAssessmentResultDocument,
  SelfAssessmentSavedAnswersDocument,
  SelfAssessmentStatementsDocument,
} from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import {
  AssessmentFlow,
  type AssessmentItem,
} from "@/components/grow/AssessmentFlow";
import { ELEMENT_CODES, elementDisplayName, elementImageSrc, elementNameId } from "@/lib/elements";
import {
  type AnswerMap,
  buildSaveResultsInput,
  joinSavedAnswers,
  LIKERT_LEVELS,
} from "@/lib/self-assessment";
import { useAgentStore } from "@/stores/agent-store";
import { logApplicationFailure } from "@/lib/user-error";
import { CODES } from "@/lib/diagnostics/contract";

// Per-element self-assessment. URL :elementId is one of the 30 CreativityElement
// values (e.g. PERSEVERANCE). The answering UI is the shared AssessmentFlow
// template; this wrapper owns the statements query, the Likert mapping, the
// incremental autosave and the hand-off to the result screen.

type SaveStatus = "idle" | "saving" | "saved" | "error";

function isKnownElement(value: string): boolean {
  return value in ELEMENT_CODES;
}

export function ElementAssessmentPage() {
  const intl = useIntl();
  const ask = useAgentStore((s) => s.ask);
  const navigate = useNavigate();
  const params = useParams<{ elementId: string }>();
  const elementParam = params.elementId ?? "";
  const valid = isKnownElement(elementParam);

  const [answers, setAnswers] = useState<AnswerMap>({});
  // Snapshot of what's persisted in this session; the diff drives the submit toast.
  const [savedAnswers, setSavedAnswers] = useState<AnswerMap>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const { data, error } = useQuery(SelfAssessmentStatementsDocument, {
    // The codegen narrows the variable type to CreativityElement; cast through
    // string because lib/elements.ts widens elements for cross-file typing.
    variables: { element: elementParam as never },
    skip: !valid,
    // Always revalidate: a stale empty result (e.g. cached before the questions
    // were imported) must not stick. genesis does not cache this resolver.
    fetchPolicy: "cache-and-network",
  });
  const { data: savedData, error: savedError } = useQuery(SelfAssessmentSavedAnswersDocument, {
    variables: { element: elementParam as never },
    skip: !valid,
    fetchPolicy: "cache-and-network",
  });
  const [saveResults] = useMutation(SaveSelfAssessmentResultsDocument);
  // Pre-save snapshot of score + rank, so the result screen can show the deltas
  // (achievement.level / achievement.rank). Read from cache; refreshed on the
  // result page network-side after the save lands.
  const { data: beforeData } = useQuery(SelfAssessmentResultDocument, {
    variables: { element: elementParam as never },
    skip: !valid,
  });

  // genesis returns the statements already ordered (by sam_question_id); the
  // `order` field is unusable (declared Int! but always null), so keep server order.
  const statements = useMemo(() => data?.selfAssessmentStatements ?? [], [data?.selfAssessmentStatements]);
  const questionIds = useMemo(() => statements.map((s) => s.id), [statements]);

  // The RAW join, never inverted (spark#1029 §2, "Zwei Funktionen, nicht eine"): an
  // inverted level would pre-select the mirrored answer of every negatively worded
  // statement. Saves send only the answers changed here, never these levels.
  const persistedAnswers = useMemo(
    () => joinSavedAnswers(questionIds, savedData?.selfAssessmentSavedAnswers),
    [questionIds, savedData?.selfAssessmentSavedAnswers],
  );
  const baselineAnswers = useMemo(
    () => ({ ...persistedAnswers, ...savedAnswers }),
    [persistedAnswers, savedAnswers],
  );
  const currentAnswers = useMemo(
    () => ({ ...baselineAnswers, ...answers }),
    [answers, baselineAnswers],
  );
  const changedQuestionIds = useMemo(
    () => questionIds.filter((id) => answers[id] !== undefined && answers[id] !== baselineAnswers[id]),
    [answers, baselineAnswers, questionIds],
  );

  // The 5-point Likert, each option labelled. The chosen level (1..5) is
  // stored; mapped to a 0-100 scorePercent only on save (lib/self-assessment).
  // Reverse questions (invertResult) are handled server-side, so we send the
  // raw selection.
  const items: AssessmentItem[] = useMemo(
    () =>
      statements.map((s) => ({
        id: s.id,
        text: `„${s.content.localized ?? ""}"`,
        options: LIKERT_LEVELS.map((l) => ({
          key: String(l.level),
          title: l.level,
          label: intl.formatMessage({ id: l.labelId }),
        })),
      })),
    [statements, intl],
  );
  const selected = useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const id of questionIds) {
      out[id] = currentAnswers[id] !== undefined ? String(currentAnswers[id]) : undefined;
    }
    return out;
  }, [currentAnswers, questionIds]);

  const total = statements.length;
  const answeredCount = questionIds.reduce(
    (n, id) => (currentAnswers[id] !== undefined ? n + 1 : n),
    0,
  );
  const resumeIndex = useMemo(() => {
    const firstOpen = questionIds.findIndex((id) => currentAnswers[id] === undefined);
    return firstOpen >= 0 ? firstOpen : 0;
  }, [currentAnswers, questionIds]);
  const complete = total > 0 && answeredCount === total;
  const dirty = changedQuestionIds.length > 0;
  const statementsPending = data?.selfAssessmentStatements === undefined && !error;
  const savedAnswersPending = savedData?.selfAssessmentSavedAnswers === undefined && !savedError;

  // Reveal: image is fully transparent + heavily blurred at 0 answered, and
  // turns fully opaque + sharp once every statement is rated. Blur keeps it
  // unrecognisable until late so the user stays curious.
  const revealSrc = elementImageSrc(elementParam);
  const revealProgress = total > 0 ? answeredCount / total : 0;
  const revealOpacity = revealProgress;
  const revealBlur = 20 * (1 - revealProgress);

  const submit = useCallback(async () => {
    if (!complete || saveStatus === "saving") return;
    try {
      if (changedQuestionIds.length > 0) {
        setSaveStatus("saving");
        const delta: AnswerMap = {};
        for (const id of changedQuestionIds) delta[id] = answers[id]!;
        await saveResults({
          variables: { input: buildSaveResultsInput(delta, changedQuestionIds) },
        });
        setSavedAnswers((prev) => ({ ...prev, ...delta }));
      }
      // Hand the pre-save score + rank to the result screen so it can show the
      // gained score / new rank against the fresh (network-refetched) values.
      navigate(`/grow/${elementParam}/result`, {
        state: {
          prevScore: beforeData?.growRanking.score ?? null,
          prevRank: beforeData?.growRanking.userRank ?? null,
        },
      });
    } catch {
      logApplicationFailure("grow.save-self-assessment");
      setSaveStatus("error");
    }
  }, [answers, beforeData, changedQuestionIds, complete, elementParam, navigate, saveResults, saveStatus]);

  // Incremental autosave: genesis upserts per (question, person) and never
  // deletes, so persisting partial progress is safe. Debounced; only the
  // answers that changed since the last persist are sent, so re-answering one
  // question writes one row, not the whole set. This is what makes the Grow
  // dashboard's "x/y answered" reflect reality between visits.
  useEffect(() => {
    const pending = changedQuestionIds;
    if (pending.length === 0) return;
    const timer = setTimeout(async () => {
      const delta: AnswerMap = {};
      for (const id of pending) delta[id] = answers[id];
      setSaveStatus("saving");
      try {
        await saveResults({ variables: { input: buildSaveResultsInput(delta, pending) } });
        setSavedAnswers((prev) => ({ ...prev, ...delta }));
        setSaveStatus("saved");
      } catch {
        logApplicationFailure("grow.autosave-self-assessment", {
          code: CODES.application.AUTOSAVE_FAILURE,
        });
        setSaveStatus("error");
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [answers, changedQuestionIds, saveResults]);

  if (!valid) {
    return (
      <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="SelfAssessmentStatements">
        <h1 id="grow-unknown-element" className="smycles-page-title">
          <FormattedMessage id="grow.unknownElement" defaultMessage="Unknown element" />
        </h1>
        <p className="mt-2 text-smycles-body text-ink-muted">{elementParam}</p>
      </section>
    );
  }

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="SelfAssessmentStatements, SelfAssessmentSavedAnswers, SelfAssessmentResult, SaveSelfAssessmentResults"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <p className="text-smycles-meta tracking-wide text-ink-muted">
            <FormattedMessage id="grow.title" defaultMessage="Grow" />
          </p>
          <h1 className="smycles-page-title mt-1">{intl.formatMessage({ id: elementNameId(elementParam), defaultMessage: elementDisplayName(elementParam) })}</h1>
          <p className="mt-2 text-smycles-body text-ink-muted">
            <FormattedMessage
              id="grow.assessmentSubtitle"
              defaultMessage="Rate yourself on each statement below."
            />
          </p>
        </div>
        {revealSrc ? (
          // The power artwork starts fully hidden and uncovers itself one
          // answered question at a time, so the user gets curious what it is.
          <div className="relative hidden aspect-[16/10] w-64 shrink-0 overflow-hidden rounded-xl bg-ink/[0.06] ring-1 ring-ink/10 @sm:block">
            <img
              src={revealSrc}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover transition-all duration-700 ease-out"
              style={{ opacity: revealOpacity, filter: `blur(${revealBlur}px)` }}
            />
          </div>
        ) : null}
      </header>

      {statementsPending || savedAnswersPending ? (
        <p className="py-8 text-smycles-body text-ink-muted">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : total === 0 ? (
        <p className="py-8 text-smycles-body text-ink-muted">
          <FormattedMessage id="grow.noQuestions" defaultMessage="No questions for this element yet." />
        </p>
      ) : (
        <AssessmentFlow
          key={elementParam}
          items={items}
          selected={selected}
          onSelect={(id, key) => setAnswers((prev) => ({ ...prev, [id]: Number(key) }))}
          onAskAboutItem={(item) => {
            // Always open the drawer (right side) + send — `ask` sets open:true and queues the prompt.
            // Prompt with the RAW statement (item.text has the „ " quote wrapper baked in), randomizing
            // among 10 equivalent phrasings (grow.elementAssessment.askIntro.0..9) for variety; each one
            // asks how to rate the statement and what it means (spark#1218).
            const raw = statements.find((s) => s.id === item.id)?.content.localized ?? item.text;
            const v = Math.floor(Math.random() * 10);
            ask(intl.formatMessage({ id: `grow.elementAssessment.askIntro.${v}` }, { statement: raw }));
          }}
          itemLabel={(index) => (
            <FormattedMessage
              id="grow.elementAssessment.statementLabel"
              defaultMessage="Statement {index}"
              values={{ index }}
            />
          )}
          statusSuffix={
            dirty && saveStatus === "error" ? (
              <span className="text-ink-accent">
                {" "}
                · <FormattedMessage id="grow.elementAssessment.notSaved" defaultMessage="not saved" />
              </span>
            ) : dirty || saveStatus === "saving" ? (
              <span className="text-ink-muted">
                {" "}
                · <FormattedMessage id="grow.elementAssessment.savingStatus" defaultMessage="saving…" />
              </span>
            ) : answeredCount > 0 ? (
              <span className="text-ink-muted">
                {" "}
                · <FormattedMessage id="grow.elementAssessment.savedStatus" defaultMessage="saved" />
              </span>
            ) : null
          }
          saving={dirty && saveStatus === "saving"}
          saveError={dirty && saveStatus === "error"}
          initialIndex={resumeIndex}
          submitLabel={
            <FormattedMessage id="grow.elementAssessment.toEvaluation" defaultMessage="To evaluation" />
          }
          submitTitle={intl.formatMessage({
            id: "grow.elementAssessment.toEvaluation",
            defaultMessage: "To evaluation",
          })}
          onSubmit={() => void submit()}
        />
      )}
    </section>
  );
}
